/*
 * Google Analytics 4 (vía vue-gtag) con consentimiento de cookies.
 *
 * - Solo se activa en producción (main.js). En desarrollo, track() escribe el
 *   evento en la consola para poder comprobarlo sin enviar nada a Google.
 * - Consent Mode v2: GA mide desde la primera visita; hasta que el visitante acepta,
 *   funciona SIN cookies
 *   (analytics_storage = denied). El consentimiento por defecto se fija en
 *   public/index.html, antes de cargar GA.
 * - Las visitas a páginas las envía vue-gtag en cada cambio de ruta; aquí se
 *   definen los títulos legibles y los eventos de interacción propios.
 */

import Vue from "vue";

export const GA_ID = "G-WT5KRVZTJQ";
export const CLAVE_CONSENTIMIENTO = "cookies_analitica"; // "granted" | "denied"

// Rutas que no se miden (panel de administración)
export const RUTAS_EXCLUIDAS = ["dashboard", "login"];

export const analiticaActiva = process.env.NODE_ENV === "production" && process.env.VUE_APP_ANALYTICS !== "off";

// Títulos de página para los informes (vue-meta actualiza document.title más tarde)
const TITULOS = {
  inicio: "Inicio",
  vistaNoticias: "Noticias",
  noticia: "Noticia",
  Eventos: "Calendario de eventos",
  Multimedia: "Galería",
  carrete: "Álbum de fotos",
  CaritasParroquialNoticias: "Cáritas parroquial",
  caritasColabora: "Colabora con Cáritas",
  historia: "Historia y patrimonio",
  sanJudas: "San Judas Tadeo",
};

export function plantillaPagina(to) {
  let titulo = TITULOS[to.name] || to.name || "Página";
  if (to.name === "carrete" && to.query.title) titulo += `: ${String(to.query.title).replace(/_/g, " ")}`;
  return {
    page_title: titulo,
    page_path: to.fullPath,
    page_location: window.location.origin + to.fullPath,
  };
}

// ---------------------------------------------------------------- Eventos

export function track(evento, params = {}) {
  if (!evento) return;
  if (!analiticaActiva) {
    if (process.env.NODE_ENV !== "production") console.debug("[analítica]", evento, params);
    return;
  }
  if (typeof window.gtag === "function") window.gtag("event", evento, params);
}

/**
 * v-track:nombre_evento="{ parametro: valor }" — envía el evento al hacer clic.
 * Ejemplo: <a v-track:contacto="{ metodo: 'telefono', origen: 'pie' }" href="tel:…">
 */
export const trackDirective = {
  bind(el, binding) {
    el.__track = { evento: binding.arg, params: binding.value };
    el.__trackClick = () => track(el.__track.evento, el.__track.params || {});
    el.addEventListener("click", el.__trackClick);
  },
  update(el, binding) {
    el.__track = { evento: binding.arg, params: binding.value };
  },
  unbind(el) {
    el.removeEventListener("click", el.__trackClick);
    delete el.__track;
    delete el.__trackClick;
  },
};

// Búsquedas: se envían cuando el visitante deja de escribir (no letra a letra)
const temporizadoresBusqueda = {};
export function trackBusqueda(seccion, termino) {
  clearTimeout(temporizadoresBusqueda[seccion]);
  const texto = (termino || "").trim();
  if (texto.length < 3) return;
  temporizadoresBusqueda[seccion] = setTimeout(() => {
    track("search", { search_term: texto.toLowerCase(), seccion });
  }, 1500);
}

// ---------------------------------------------------------------- Consentimiento

export const estadoConsentimiento = Vue.observable({
  valor: leerConsentimiento(), // "granted" | "denied" | null (sin decidir)
  panelAbierto: false,
});

function leerConsentimiento() {
  try {
    const v = localStorage.getItem(CLAVE_CONSENTIMIENTO);
    return v === "granted" || v === "denied" ? v : null;
  } catch (e) {
    return null;
  }
}

export function guardarConsentimiento(valor) {
  try {
    localStorage.setItem(CLAVE_CONSENTIMIENTO, valor);
  } catch (e) {
    // localStorage no disponible: se aplica solo a esta visita
  }
  estadoConsentimiento.valor = valor;
  estadoConsentimiento.panelAbierto = false;

  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", { analytics_storage: valor });
  }
  if (valor === "denied") borrarCookiesGA();
}

export function abrirPanelCookies() {
  estadoConsentimiento.panelAbierto = true;
}

// Al retirar el consentimiento se eliminan las cookies que ya hubiera puesto GA
function borrarCookiesGA() {
  const dominios = ["", window.location.hostname, "." + window.location.hostname.replace(/^www\./, "")];
  document.cookie
    .split(";")
    .map((c) => c.split("=")[0].trim())
    .filter((nombre) => nombre === "_ga" || nombre.startsWith("_ga_") || nombre === "_gid" || nombre === "_gat")
    .forEach((nombre) => {
      dominios.forEach((dominio) => {
        document.cookie = `${nombre}=; Max-Age=0; path=/${dominio ? "; domain=" + dominio : ""}`;
      });
    });
}

// ---------------------------------------------------------------- Seguimiento automático
/*
 * Se activa una sola vez al arrancar (main.js) y mide sin tocar los componentes:
 *  - clic:               cada clic en algo interactivo (enlace, botón, imagen, tarjeta…),
 *                        con qué es, su texto, a dónde lleva y en qué zona de la página está.
 *  - scroll_profundidad: hasta dónde baja el visitante en cada página (25, 50, 75, 90 y 100 %).
 *  - navegacion:         de qué página a qué página pasa dentro de la web.
 *  - copiar_texto:       si copia texto de la página (solo la longitud, no el contenido).
 * Se envía aunque el visitante rechace las cookies: en ese caso GA recibe los
 * eventos SIN cookies ni identificador (Consent Mode v2, ver public/index.html).
 * Nunca se envía lo que se escribe en formularios.
 */

const SELECTOR_CLICABLE = [
  "a[href]",
  "button",
  "[role='button']",
  "[role='tab']",
  "[role='menuitem']",
  "[data-track-clic]",
  "summary",
  "img",
  "select",
  "input[type='checkbox']",
  "input[type='radio']",
  "input[type='submit']",
  "input[type='button']",
  "label",
  ".card",
  "[onclick]",
].join(",");

const ZONAS = ["header", "nav", "footer", "aside", "main", "[role='dialog']", ".modal"];
const UMBRALES_SCROLL = [25, 50, 75, 90, 100];
const MAX = 100; // GA4 corta los valores de los parámetros a 100 caracteres

const recortar = (texto, max = MAX) => String(texto || "").replace(/\s+/g, " ").trim().slice(0, max);

function rutaExcluida(route) {
  return !!route && (RUTAS_EXCLUIDAS.includes(route.name) || /^\/(dashboard|login)/i.test(route.path || ""));
}

function tipoElemento(el) {
  const tag = el.tagName.toLowerCase();
  if (tag === "a") return "enlace";
  if (tag === "img") return "imagen";
  if (tag === "select") return "desplegable";
  if (tag === "input") return el.type === "checkbox" || el.type === "radio" ? "casilla" : "boton";
  if (tag === "label") return "etiqueta";
  if (tag === "summary") return "desplegable";
  if (el.classList.contains("card")) return "tarjeta";
  const rol = el.getAttribute("role");
  if (rol === "tab") return "pestaña";
  if (rol === "menuitem") return "menu";
  return "boton";
}

function textoElemento(el) {
  return recortar(
    el.getAttribute("data-track-clic") ||
      el.getAttribute("aria-label") ||
      (el.tagName === "IMG" ? el.getAttribute("alt") : "") ||
      el.getAttribute("title") ||
      (el.tagName === "SELECT" ? "" : el.innerText || el.textContent) ||
      el.querySelector?.("img[alt]")?.getAttribute("alt") ||
      el.getAttribute("name") ||
      el.id
  );
}

// Zona de la página: la sección con id o título más cercana, o header/nav/footer…
function zonaElemento(el) {
  const conId = el.closest("[data-seccion], section[id], [id]:not([id^='__']):not(#app)");
  if (conId) return recortar(conId.getAttribute("data-seccion") || conId.id, 60);
  const seccion = el.closest("section, article");
  const titulo = seccion?.querySelector("h1, h2, h3");
  if (titulo) return recortar(titulo.textContent, 60);
  for (const z of ZONAS) {
    const contenedor = el.closest(z);
    if (contenedor) return z.replace(/[[\]'=.]/g, "").replace("roledialog", "modal");
  }
  return "pagina";
}

function destinoElemento(el) {
  if (el.tagName === "IMG") return recortar(el.currentSrc || el.src);
  const href = el.closest("a[href]")?.getAttribute("href");
  if (!href || href === "#") return "";
  try {
    const url = new URL(href, window.location.href);
    if (url.origin === window.location.origin) return recortar(url.pathname + url.search);
    return recortar(url.href);
  } catch (e) {
    return recortar(href);
  }
}

function alHacerClic(router, event) {
  if (rutaExcluida(router.currentRoute)) return;
  const el = event.target.closest?.(SELECTOR_CLICABLE);
  if (!el) return;

  const destino = destinoElemento(el);
  const externo = /^(https?:|mailto:|tel:)/i.test(destino) && !destino.startsWith(window.location.origin);
  track("clic", {
    elemento: tipoElemento(el),
    texto_clic: textoElemento(el) || "(sin texto)",
    zona: zonaElemento(el),
    destino,
    enlace_externo: externo ? "si" : "no",
    pagina: recortar(router.currentRoute.fullPath),
  });
}

function seguimientoScroll(router) {
  let enviados = new Set();
  let pendiente = false;

  const medir = () => {
    pendiente = false;
    if (rutaExcluida(router.currentRoute)) return;
    const doc = document.documentElement;
    const alto = doc.scrollHeight - window.innerHeight;
    // Páginas que caben en la pantalla: no hay scroll que medir
    if (alto < 200) return;
    const porcentaje = Math.min(100, Math.round((window.scrollY / alto) * 100));
    UMBRALES_SCROLL.forEach((umbral) => {
      if (porcentaje >= umbral && !enviados.has(umbral)) {
        enviados.add(umbral);
        track("scroll_profundidad", { porcentaje: umbral, pagina: recortar(router.currentRoute.fullPath) });
      }
    });
  };

  window.addEventListener(
    "scroll",
    () => {
      if (pendiente) return;
      pendiente = true;
      window.requestAnimationFrame(medir);
    },
    { passive: true }
  );

  // Cada página empieza de cero
  router.afterEach(() => {
    enviados = new Set();
  });
}

let seguimientoIniciado = false;

export function iniciarSeguimiento(router) {
  if (seguimientoIniciado || typeof window === "undefined") return;
  seguimientoIniciado = true;

  // Fase de captura: se registra aunque el componente detenga la propagación del clic
  document.addEventListener("click", (event) => alHacerClic(router, event), true);

  seguimientoScroll(router);

  router.afterEach((to, from) => {
    if (rutaExcluida(to) || !from.name) return; // la primera carga ya es la page_view
    track("navegacion", {
      desde: recortar(from.fullPath),
      hacia: recortar(to.fullPath),
      seccion_destino: TITULOS[to.name] || to.name || "",
    });
  });

  document.addEventListener("copy", () => {
    if (rutaExcluida(router.currentRoute)) return;
    const longitud = String(window.getSelection?.() || "").length;
    if (longitud) track("copiar_texto", { longitud, pagina: recortar(router.currentRoute.fullPath) });
  });
}
